(() => {
  'use strict';

  /* PN_PHASE5_FINANCE_V2 */

  let started = false;
  let paymentRows = [];
  let paymentSummary = {};
  let paymentMode = 'accounts';
  let accountDetail = 'all';
  let accountEvents = [];
  let accountRegistrationEvents = new Map();
  let accountEventsStatus = 'loading';
  let accountRolesState = 'checking';
  let paymentCategory = 'all';
  let paymentSearch = '';
  let paymentMentorRows = [];
  let paymentPayoutRows = [];
  let paymentExtraState = { dues: 'idle', payouts: 'idle' };

  document.addEventListener('peernest:admin-finance-target', event => {
    const requested = event?.detail?.mode;
    if (!['accounts','transactions','dues','payouts','refunds','issues','revenue','pricing'].includes(requested)) return;
    paymentMode=requested;
    const panel=document.querySelector('[data-pn5-payment-center]');
    if(panel) {
      renderPaymentCenter(panel);
      if(requested==='dues'||requested==='payouts') loadPaymentExtra(panel,requested);
      if(requested==='pricing') loadMentorPricing(panel);
    }
  });


  let mentorRows = [];
  let mentorSearch = '';

  const esc = value =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');


  function money(
    value,
    currency = 'INR'
  ) {

    const number =
      Number(value || 0);

    return new Intl.NumberFormat(
      'en-IN',
      {
        style:'currency',
        currency:
          currency || 'INR',
        minimumFractionDigits:
          number % 1 ? 2 : 0,
        maximumFractionDigits:2
      }
    ).format(number);

  }


  function dateText(value) {

    if (!value) {
      return '—';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '—';
    }

    return date.toLocaleString(
      undefined,
      {
        dateStyle:'medium',
        timeStyle:'short'
      }
    );

  }


  function statusText(value) {

    return String(
      value || 'unknown'
    )
      .replaceAll('_', ' ')
      .replace(
        /\b\w/g,
        letter =>
          letter.toUpperCase()
      );

  }


  function pill(value) {

    const state =
      String(
        value || 'unknown'
      ).toLowerCase();

    return `
      <span
        class="pn5-fin-pill"
        data-state="${esc(state)}"
      >
        ${esc(
          statusText(state)
        )}
      </span>
    `;

  }


  async function rpc(
    name,
    body = {}
  ) {

    return window.PeerNestDB
      .request(
        `rpc/${name}`,
        '',
        {
          method:'POST',
          body
        }
      );

  }


  /* Read-only display reconciliation: the dashboard's older statistics RPC
     currently excludes some accounts from student/mentor role subtotals.
     Never overwrite user counts unless the independent full list reconciles. */
  async function reconcileAdminRoleCards(overview) {
    if (!overview || !overview.querySelector('[data-pnadm-students]')) return;
    try {
      const people = await rpc('admin_list_users');
      if (!Array.isArray(people)) return;
      const counts = { student:0, mentor:0, admin:0 };
      for (const person of people) {
        const role = String(person.role ?? person.user_role ?? '').toLowerCase();
        if (!(role in counts)) return; // Never guess missing/unknown roles.
        counts[role] += 1;
      }
      const totalNode=overview.querySelector('[data-pnadm-total-users]');
      const originalTotal=Number(totalNode?.textContent?.trim());
      if (counts.student+counts.mentor+counts.admin !== people.length ||
          !Number.isFinite(originalTotal) || originalTotal !== people.length) return;
      const fields={student:'[data-pnadm-students]', mentor:'[data-pnadm-mentors]',admin:'[data-pnadm-admins]'};
      for (const [role,selector] of Object.entries(fields)) {
        const node=overview.querySelector(selector);
        if (node) node.textContent=String(counts[role]);
      }
      accountRolesState='reconciled';
    } catch (err) {
      console.info('[PeerNest Admin] Role card cross-check unavailable; keeping original stats.');
    }
  }

  /* An event can have zero paid registrations. Listing real events permits
     navigation into a genuine empty transaction history without inventing money. */
  async function loadFinanceEventCatalog() {
    if (!window.PeerNestDB?.request) return;
    try {
      const [events, registrations] = await Promise.all([
        window.PeerNestDB.request('events', 'select=id,title,starts_at&order=starts_at.desc&limit=250'),
        window.PeerNestDB.request('event_registrations','select=id,event_id&order=created_at.desc&limit=2000')
      ]);
      accountEvents=Array.isArray(events) ? events.filter(e=>e?.id) : [];
      accountRegistrationEvents=new Map((Array.isArray(registrations) ? registrations : [])
        .filter(r=>r?.id && r?.event_id).map(r=>[String(r.id),String(r.event_id)]));
      accountEventsStatus='loaded';
    } catch (err) {
      accountEventsStatus='error';
      console.warn('[PeerNest Admin] Event catalog unavailable; showing recorded transaction groups only.');
    }
  }

  /* ========================================================
     MENTOR EARNINGS
  ======================================================== */

  async function renderMentorFinance() {

    const view =
      document.querySelector(
        '[data-pnmd-view="earnings"]'
      );

    if (!view) {
      return;
    }


    try {

      const data =
        await rpc(
          'get_my_mentor_finance_v2'
        );


      const summary =
        data?.summary || {};

      const items =
        Array.isArray(
          data?.items
        )
          ? data.items
          : [];

      const currency =
        summary.currency ||
        'INR';


      view.innerHTML = `
        <div class="pnmd-view-head">

          <div>

            <span>
              EARNINGS
            </span>

            <h2>
              Your mentor earnings
            </h2>

            <p>
              Earnings are counted only after
              a session is successfully completed.
            </p>

          </div>

        </div>


        <div class="pn5-fin-grid pn5-fin-grid--3">

          <article class="pn5-fin-card pn5-fin-card--primary">

            <span>
              TOTAL EARNINGS
            </span>

            <strong>
              ${money(
                summary.total_earnings,
                currency
              )}
            </strong>

            <small>
              Completed sessions only
            </small>

          </article>


          <article class="pn5-fin-card">

            <span>
              PAID
            </span>

            <strong>
              ${money(
                summary.paid,
                currency
              )}
            </strong>

            <small>
              Confirmed mentor payouts
            </small>

          </article>


          <article class="pn5-fin-card">

            <span>
              TO BE PAID
            </span>

            <strong>
              ${money(
                summary.to_be_paid,
                currency
              )}
            </strong>

            <small>
              Earned and awaiting payout
            </small>

          </article>

        </div>


        ${
          Number(
            summary.pending_sessions ||
            0
          ) > 0
            ? `
              <div class="pn5-fin-note">

                ${esc(
                  summary.pending_sessions
                )}
                confirmed session${
                  Number(
                    summary.pending_sessions
                  ) === 1
                    ? ''
                    : 's'
                }
                are still pending completion
                and are not counted in Total Earnings.

              </div>
            `
            : ''
        }


        <article class="pnmd-view-card pnmd-view-card--wide">

          <div class="pn5-fin-section-head">

            <div>

              <span>
                SESSION HISTORY
              </span>

              <h3>
                Earnings breakdown
              </h3>

            </div>

          </div>


          ${
            items.length
              ? `
                <div class="pn5-fin-table-wrap">

                  <table class="pn5-fin-table">

                    <thead>

                      <tr>
                        <th>Session</th>
                        <th>Student</th>
                        <th>Gross</th>
                        <th>Your 51%</th>
                        <th>Status</th>
                        <th>Payout</th>
                      </tr>

                    </thead>

                    <tbody>

                      ${items
                        .map(
                          item => `
                            <tr>

                              <td>
                                ${esc(
                                  dateText(
                                    item.starts_at
                                  )
                                )}
                              </td>

                              <td>

                                <strong>
                                  ${esc(
                                    item.student_name ||
                                    'Student'
                                  )}
                                </strong>

                                <small>
                                  ${esc(
                                    item.student_email ||
                                    ''
                                  )}
                                </small>

                              </td>

                              <td>
                                ${money(
                                  item.gross_amount,
                                  item.currency
                                )}
                              </td>

                              <td>
                                <strong>
                                  ${money(
                                    item.mentor_amount,
                                    item.currency
                                  )}
                                </strong>
                              </td>

                              <td>
                                ${pill(
                                  item.earning_status
                                )}
                              </td>

                              <td>

                                ${
                                  item.payout_reference
                                    ? `
                                      <strong>
                                        ${esc(
                                          item.payout_reference
                                        )}
                                      </strong>
                                    `
                                    : '—'
                                }

                              </td>

                            </tr>
                          `
                        )
                        .join('')}

                    </tbody>

                  </table>

                </div>
              `
              : `
                <div class="pn5-fin-empty">
                  No mentor earning records yet.
                </div>
              `
          }

        </article>
      `;

    }
    catch (error) {

      view.innerHTML = `
        <div class="pn5-fin-empty">
          ${
            esc(
              error?.message ||
              'Could not load earnings.'
            )
          }
        </div>
      `;

    }

  }



  /* ========================================================
     ADMIN OVERVIEW — clear single ledger, v5
     Registered users and original mentor cards are above this component.
     One Mentoring total: Individual and Compare are subdivisions, never
     adjacent sources of income. A processed refund reduces NET, not GROSS.
     The older chart widget is intentionally visually suppressed by the
     scoped stylesheet in the existing Admin section (it uses stale labels).
  ======================================================== */
  async function renderAdminOverview() {
    const dashboard = document.querySelector('[data-pnadm-dashboard]');
    const overview = dashboard?.querySelector('[data-pnadm-view="overview"]');
    if (!overview) return;
    try {
      const [summary, operations] = await Promise.all([
        rpc('admin_finance_summary_v2'),
        rpc('admin_list_payment_operations_v2')
      ]);
      if (!summary || typeof summary.total_gross_earnings === 'undefined' || !Array.isArray(operations)) {
        throw new Error('Finance summary or transaction history is unavailable.');
      }
      const currency=summary.currency || 'INR';
      const ledger=financeLedger(operations);
      const mentoring=operations.filter(row=>['mentor_booking','compare_live'].includes(row.purpose));
      const individual=operations.filter(row=>row.purpose==='mentor_booking');
      const compareRows=operations.filter(row=>row.purpose==='compare_live');
      const eventRows=operations.filter(row=>row.purpose==='event_registration');
      const mentoringLedger=financeLedger(mentoring);
      const individualLedger=financeLedger(individual);
      const compareLedger=financeLedger(compareRows);
      const eventLedger=financeLedger(eventRows);
      const paidMentoring=mentoring.filter(validPaidPayment);
      const mentorAllocated=allocation(paidMentoring,'mentor_amount');
      const platformAllocated=allocation(paidMentoring,'platform_amount');
      const reportedGross=Number(summary.total_gross_earnings);
      const paidRowsGross=amountTotal(operations.filter(validPaidPayment));
      const mismatch=!Number.isFinite(reportedGross) || Math.abs(reportedGross-paidRowsGross) > 0.011;
      let section=overview.querySelector('[data-pn5-admin-finance]');
      if(!section){
        section=document.createElement('section');
        section.className='pnadm-card pn5-admin-finance';
        section.setAttribute('data-pn5-admin-finance','');
      }
      overview.appendChild(section);
      section.innerHTML=`
        <div class="pnadm-card__head pn5-overview-head"><div>
          <span>PAYMENT OVERVIEW</span><h3>Money at a glance</h3>
          <p>One view of collections, refunds and what is payable.</p>
        </div><button type="button" class="pn5-fin-action" data-pnfinal-open-breakdown>Full accounts →</button></div>
        <div class="pn5-fin-grid pn5-fin-grid--4 pn5-overview-main-metrics">
          <article class="pn5-fin-card"><span>GROSS COLLECTED</span><strong>${money(ledger.gross,currency)}</strong><small>Successful captured payments</small></article>
          <article class="pn5-fin-card"><span>REFUNDED</span><strong>${money(ledger.refunded,currency)}</strong><small>Confirmed refunds only</small></article>
          <article class="pn5-fin-card pn5-fin-card--primary"><span>NET COLLECTED</span><strong>${money(ledger.net,currency)}</strong><small>Gross minus completed refunds</small></article>
          <article class="pn5-fin-card"><span>MENTOR DUES</span><strong>${money(summary.mentor_due,currency)}</strong><small>Completed, eligible and unpaid</small></article>
        </div>
        ${mismatch?`<p class="pn5-fin-audit-warning" role="alert">Please reconcile collections: the finance summary reports ${money(reportedGross,currency)} but the current verified, fulfilled transaction list totals ${money(paidRowsGross,currency)}.</p>`:''}
        <div class="pn5-fin-section-head"><div><span>COLLECTIONS</span><h3>Where the money came from</h3></div></div>
        <div class="pn5-fin-grid pn5-fin-grid--2 pn5-overview-category-grid">
          <article class="pn5-fin-card pn5-overview-parent-card">
            <span>MENTORING · TOTAL</span><strong>${money(mentoringLedger.gross,currency)}</strong>
            <small>One mentoring total, broken down below</small>
            <div class="pn5-overview-subrows">
              <div><span>Individual · Quick / Deep Dive</span><b>${money(individualLedger.gross,currency)}</b></div>
              <div><span>Compare Live · Two mentors</span><b>${money(compareLedger.gross,currency)}</b></div>
            </div>
          </article>
          <article class="pn5-fin-card pn5-overview-parent-card">
            <span>EVENTS · TOTAL</span><strong>${money(eventLedger.gross,currency)}</strong>
            <small>PeerNest is the organizer</small>
            <div class="pn5-overview-subrows"><div><span>Event transactions and refunds</span><button type="button" class="pn5-fin-link" data-pnfinal-open-events>Open events →</button></div></div>
          </article>
        </div>
        <div class="pn5-overview-allocation">
          <div class="pn5-overview-allocation-heading"><span>MENTORING SHARE</span><strong>Allocated ≠ earned</strong></div>
          <div class="pn5-overview-share-grid">
            <div><span>Mentors · allocated</span><strong>${displayAllocation(mentorAllocated,currency)}</strong><small>Includes upcoming sessions; not yet payable</small></div>
            <div><span>PeerNest · allocated</span><strong>${displayAllocation(platformAllocated,currency)}</strong><small>Expected share, subject to session outcomes</small></div>
            <div><span>PeerNest · earned</span><strong>${money(summary.booking_platform_retained,currency)}</strong><small>Recognized mentoring revenue</small></div>
          </div>
          <p>PeerNest revenue earned including events: <b>${money(summary.peerNest_retained,currency)}</b> · Mentor payouts recorded: <b>${money(summary.mentor_paid,currency)}</b>.</p>
        </div>
        <p class="pn5-overview-fineprint">Transactions and refund history are preserved under Payments. Pending allocations do not become mentor dues until eligible. Communities have no verified paid checkout.</p>
      `;
      bindOverviewActions(overview);
      void reconcileAdminRoleCards(overview);
    } catch(error) {
      console.error('[PeerNest] Admin finance overview',error);
      let section=overview.querySelector('[data-pn5-admin-finance]');
      if(!section){
        section=document.createElement('section');section.className='pnadm-card pn5-admin-finance';
        section.setAttribute('data-pn5-admin-finance','');overview.appendChild(section);
      }
      section.innerHTML='<div class="pn5-fin-audit-warning" role="alert">Financial Overview could not load. Please refresh or open Payments. No missing amounts are being shown as ₹0.</div>';
    }
  }

  /* ========================================================
     ADMIN PAYMENT CENTER
  ======================================================== */

  function filteredPaymentRows() {
    const query = paymentSearch.trim().toLowerCase();
    let rows = paymentRows;
    if (paymentMode === 'transactions') {
      if (paymentCategory === 'mentoring') {
        rows = rows.filter(row => ['mentor_booking','compare_live'].includes(String(row.purpose || '')));
      } else if (paymentCategory === 'compare') {
        rows = rows.filter(row => row.purpose === 'compare_live');
      } else if (paymentCategory === 'events') {
        rows = rows.filter(row => row.purpose === 'event_registration');
      }
    }
    if (!query) return rows;
    return rows.filter(row => {
      try { return JSON.stringify(row).toLowerCase().includes(query); }
      catch (_) { return false; }
    });
  }


  // Reporting only. These figures never change stored payment or earning statuses.
  function validPaidPayment(row) {
    const meta = row?.metadata || {};
    return String(row?.status || '') === 'paid'
      && String(meta.fulfilled || '') === 'true'
      && String(meta.requires_refund || 'false') !== 'true';
  }

  function compareAllocationSummary(rows) {
    const valid = (rows || []).filter(row =>
      row.purpose === 'compare_live' && validPaidPayment(row)
    );
    return {
      gross: valid.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      mentor: valid.reduce((sum, row) => sum + Number(row.mentor_amount || 0), 0),
      platform: valid.reduce((sum, row) => sum + Number(row.platform_amount || 0), 0),
      count: valid.length
    };
  }

  function categoryLabel(row) {
    if (row.purpose === 'compare_live') return 'Compare Live';
    if (row.purpose === 'mentor_booking') {
      const duration = (new Date(row.session_ends_at) - new Date(row.session_starts_at)) / 60000;
      return duration === 15 ? 'Quick' : duration === 30 ? 'Deep Dive' : 'Individual Mentoring';
    }
    if (row.purpose === 'event_registration') return 'Event';
    return statusText(row.purpose || 'Other');
  }


  function bindOverviewActions(overview) {

    function openFinance(detail){
      paymentMode='accounts'; accountDetail=detail;
      document.querySelector('[data-pnadm-nav="payments"]')?.click();
      document.dispatchEvent(new CustomEvent('peernest:admin-finance-target',{detail:{mode:'accounts'}}));
      const panel=document.querySelector('[data-pn5-payment-center]');
      if(panel)renderPaymentCenter(panel);
    }
    overview.querySelector('[data-pnfinal-open-breakdown]')?.addEventListener('click',()=>openFinance('all'));
    overview.querySelector('[data-pnfinal-open-events]')?.addEventListener('click',()=>openFinance('events'));
    overview.querySelector('[data-pnfinal-open-dues]')?.addEventListener('click', () => {
      document.querySelector('[data-pnadm-nav="payments"]')?.click();
      document.dispatchEvent(new CustomEvent('peernest:admin-finance-target',{detail:{mode:'dues'}}));
    });
    overview.querySelectorAll('[data-pn5-pay-mentor]').forEach(button => {
      button.addEventListener('click', () => {
        const id=button.dataset.pn5PayMentor;
        if(id && typeof window.PeerNestManualPayout?.open === 'function') window.PeerNestManualPayout.open(id);
        else window.alert('Payout form unavailable. Refresh Admin and retry.');
      });
    });
  }

  function paymentIssuesHTML(rows) {
    const flagged = rows.filter(row => {
      const meta=row.metadata || {};
      return String(meta.requires_refund || 'false') === 'true' ||
        (String(row.status) === 'paid' && String(meta.fulfilled || 'false') !== 'true');
    });
    return `<p class="pn5-fin-note">Only payment records with a refund or fulfillment review flag are shown here. This is not yet a universal dispute-resolution system. Confirm any Razorpay action in the provider dashboard.</p>
      ${flagged.length ? paymentTransactionsHTML(flagged) : '<div class="pn5-fin-empty">No flagged payment records in this view.</div>'}`;
  }

  function paymentTransactionsHTML(rows) {
    if (!rows.length) return '<div class="pn5-fin-empty">No matching transactions.</div>';
    return `
      <div class="pn5-fin-table-wrap"><table class="pn5-fin-table">
      <thead><tr><th>Type</th><th>Student</th><th>Mentor / Event</th><th>Gross</th><th>Payment</th><th>Booking / Registration</th><th>Reference</th></tr></thead>
      <tbody>${rows.map(row => `
      <tr>
        <td><strong>${esc(categoryLabel(row))}</strong><small>${esc(dateText(row.created_at))}</small></td>
        <td><strong>${esc(row.student_name || '—')}</strong><small>${esc(row.student_email || '')}</small></td>
        <td><strong>${esc(row.purpose === 'event_registration' ? (row.event_title || 'Event') : (row.mentor_name || 'Mentor'))}</strong>
        <small>${esc(row.purpose === 'compare_live' ? 'Two mentors · One student payment' : '')}</small></td>
        <td><strong>${money(row.amount, row.currency)}</strong></td>
        <td>${pill(row.status)}</td>
        <td>${pill(row.purpose === 'event_registration' ? row.event_registration_status : row.booking_status)}</td>
        <td><small>${esc(row.provider_payment_id || row.razorpay_payment_id || row.id || '—')}</small></td>
      </tr>`).join('')}</tbody></table></div>`;
  }

  function paymentDuesHTML() {
    if (paymentExtraState.dues === 'loading') return '<div class="pn5-fin-empty">Loading mentor dues…</div>';
    if (paymentExtraState.dues === 'error') return '<div class="pn5-fin-empty">Could not load dues. Choose Refresh to retry.</div>';
    // The backend calculates to_be_paid from eligible/unpaid earnings.
    // Pending-session allocations never appear as dues or Pay actions.
    const rows = paymentMentorRows.filter(m => Number(m.to_be_paid || 0) > 0);
    const payable = rows.reduce((sum,m) => sum + Math.max(0,Number(m.to_be_paid || 0)),0);
    return `
      <div class="pn5-fin-grid pn5-fin-grid--3">
        <article class="pn5-fin-card pn5-fin-card--primary">
          <span>READY TO PAY</span><strong>${money(payable)}</strong>
          <small>Completed sessions and approved exceptions only</small></article>
        <article class="pn5-fin-card"><span>MENTORS TO PAY</span>
          <strong>${rows.length}</strong><small>Mentors with eligible dues</small></article>
      </div>
      <p class="pn5-fin-note">Dues appear only after a session is completed and the earning is eligible, or after an approved no-show exception. Pay opens the existing form; enter the real UTR after transferring funds.</p>
      ${rows.length ? `<div class="pn5-fin-table-wrap"><table class="pn5-fin-table">
       <thead><tr><th>Mentor</th><th>Amount due</th><th>Action</th></tr></thead>
       <tbody>${rows.map(m => `
        <tr><td><strong>${esc(m.display_name || m.account_name || 'Mentor')}</strong><small>${esc(m.email || '')}</small></td>
        <td><strong>${money(m.to_be_paid,m.currency)}</strong></td>
        <td><button type="button" class="pn5-fin-action" data-pn5-pay-mentor="${esc(m.mentor_id || m.id)}">Pay</button></td>
        </tr>`).join('')}</tbody></table></div>` : '<div class="pn5-fin-empty">All caught up — no eligible unpaid mentor dues.</div>'}`;
  }

  function paymentPayoutsHTML() {
    if (paymentExtraState.payouts === 'loading') return '<div class="pn5-fin-empty">Loading payout batches…</div>';
    if (paymentExtraState.payouts === 'error') return '<div class="pn5-fin-empty">Could not load payouts. Choose Refresh to retry.</div>';
    const rows = paymentPayoutRows;
    if (!rows.length) return '<div class="pn5-fin-empty">No payout batches recorded.</div>';
    return `<div class="pn5-fin-table-wrap"><table class="pn5-fin-table">
     <thead><tr><th>Date</th><th>Mentor</th><th>Amount</th><th>Status</th><th>UTR / Provider reference</th><th></th></tr></thead>
     <tbody>${rows.map(p => {
       const mentor = paymentMentorRows.find(m => String(m.mentor_id || m.id) === String(p.mentor_id)) || {};
       return `<tr><td>${esc(dateText(p.created_at))}</td><td><strong>${esc(p.mentor_name || mentor.display_name || mentor.account_name || 'Mentor')}</strong><small>${esc(p.mentor_id || '')}</small></td>
       <td><strong>${money(p.amount,p.currency)}</strong></td><td>${pill(p.status)}</td>
       <td><small>${esc(p.provider_utr || p.utr || p.reference_id || p.provider_payout_id || '—')}</small></td>
       <td>${p.mentor_id ? `<button type="button" class="pn5-fin-action" data-pn5-pay-mentor="${esc(p.mentor_id)}">View payout</button>` : ''}</td></tr>`;
     }).join('')}</tbody></table></div>`;
  }

  function paymentRefundsHTML(rows) {
    const refunds = rows.filter(row => {
      const meta = row.metadata || {};
      return String(row.status || '').includes('refund')
        || Number(row.refund_amount || 0)>0
        || String(meta.requires_refund || 'false') === 'true';
    });
    if (!refunds.length) return '<div class="pn5-fin-empty">No matching refund records.</div>';
    return `<div class="pn5-fin-table-wrap"><table class="pn5-fin-table">
       <thead><tr><th>Type</th><th>Student</th><th>Collected</th><th>Refunded</th><th>Payment status</th><th>Refund flag</th><th>Reference</th></tr></thead>
       <tbody>${refunds.map(row => `<tr><td><strong>${esc(categoryLabel(row))}</strong></td>
       <td>${esc(row.student_name || '—')}</td><td>${money(row.amount,row.currency)}</td>
       <td>${money(row.refund_amount,row.currency)}</td><td>${pill(row.status)}</td>
       <td>${pill(String(row.metadata?.requires_refund || 'false') === 'true' ? 'review_required' : '—')}</td>
       <td><small>${esc(row.provider_payment_id || row.razorpay_payment_id || row.id || '—')}</small></td></tr>`).join('')}</tbody></table></div>
       <p class="pn5-fin-note">Refund entries reflect PeerNest's records. Confirm actual refund settlement with Razorpay before resolving an issue.</p>`;
  }

  async function loadPaymentExtra(panel, mode) {
    if (mode !== 'dues' && mode !== 'payouts') return;
    paymentExtraState[mode] = 'loading';
    renderPaymentCenter(panel);
    try {
      const result = await rpc('admin_list_mentor_finance_v2');
      paymentMentorRows = Array.isArray(result) ? result : [];
      if (mode === 'payouts') {
        const payouts = await rpc('admin_list_mentor_payouts_v1');
        paymentPayoutRows = Array.isArray(payouts) ? payouts : [];
      }
      paymentExtraState[mode] = 'loaded';
    } catch (error) {
      console.error('[PeerNest Payments] supplemental list', error);
      paymentExtraState[mode] = 'error';
    }
    if (paymentMode === mode) renderPaymentCenter(panel);
  }


  function paymentRevenueHTML() {
    const s=paymentSummary || {};
    const currency=s.currency || 'INR';
    const mentoringRows=paymentRows.filter(row=>['mentor_booking','compare_live'].includes(row.purpose));
    const individualRows=mentoringRows.filter(row=>row.purpose==='mentor_booking');
    const compareRows=mentoringRows.filter(row=>row.purpose==='compare_live');
    const eventRows=paymentRows.filter(row=>row.purpose==='event_registration');
    const mentoring=financeLedger(mentoringRows);
    const individual=financeLedger(individualRows);
    const compare=financeLedger(compareRows);
    const events=financeLedger(eventRows);
    const c=compareAllocationSummary(paymentRows);
    return `
      <div class="pn5-fin-section-head"><div><span>REPORT DETAILS</span><h3>Collections by category</h3>
        <p>Individual and Compare Live are both inside Mentoring. Amounts are not added twice.</p></div></div>
      <div class="pn5-fin-grid pn5-fin-grid--2 pn5-overview-category-grid">
        <article class="pn5-fin-card pn5-overview-parent-card"><span>MENTORING · GROSS</span><strong>${money(mentoring.gross,currency)}</strong>
          <small>All mentoring payments</small><div class="pn5-overview-subrows">
          <div><span>Individual · Quick / Deep Dive</span><b>${money(individual.gross,currency)}</b></div>
          <div><span>Compare Live · Two mentors</span><b>${money(compare.gross,currency)}</b></div>
          </div></article>
        <article class="pn5-fin-card pn5-overview-parent-card"><span>EVENTS · GROSS</span><strong>${money(events.gross,currency)}</strong>
          <small>PeerNest organizes every event</small><div class="pn5-overview-subrows"><div><span>Net after event refunds</span><b>${money(events.net,currency)}</b></div></div>
        </article>
      </div>
      <div class="pn5-overview-allocation"><div class="pn5-overview-allocation-heading"><span>COMPARE LIVE · ALLOCATIONS</span><strong>Not yet all earned</strong></div>
        <div class="pn5-overview-share-grid">
          <div><span>Mentors allocated</span><strong>${money(c.mentor,currency)}</strong><small>From ${c.count} paid Compare booking(s)</small></div>
          <div><span>PeerNest allocated</span><strong>${money(c.platform,currency)}</strong><small>Subject to completion and outcome</small></div>
          <div><span>PeerNest earned · all services</span><strong>${money(s.peerNest_retained,currency)}</strong><small>Recognized revenue including events</small></div>
        </div>
      </div>
      <p class="pn5-fin-note">Gross, net and earnings serve different purposes. Successful refunds lower net, not the original gross history. Mentor dues are only eligible unpaid earnings; pending bookings do not appear as payable. Communities have no verified paid checkout.</p>`;
  }

  /* PEERNEST_ACCOUNTING_FINAL_V4: Accounts: no extra writes or invented community receipts. All figures derive
     from existing payment/earnings RPCs; transactions remain the source of truth. */
  /* Gross payments are an immutable cash-in history: later successful refunds
     reduce net, not original gross. Only provider-confirmed captures are eligible.
     A refund flag alone is not evidence that a refund was processed. */
  function paidCaptureRecord(row) {
    const status=String(row?.status || '').toLowerCase();
    const meta=row?.metadata || {};
    const captured=['paid','refunded','partially_refunded'].includes(status);
    return captured && Boolean(row?.provider_payment_id || row?.razorpay_payment_id || String(meta.fulfilled || '')==='true');
  }
  function financeLedger(rows=paymentRows) {
    const captured=rows.filter(paidCaptureRecord);
    const gross=amountTotal(captured);
    const refundAmount=captured.reduce((sum,row)=>{
      const status=String(row.status || '').toLowerCase();
      if (status!=='refunded' && status!=='partially_refunded') return sum;
      const amount=Math.max(0,Number(row.amount)||0);
      const supplied=Number(row.refund_amount);
      const refunded=status==='refunded' && (!Number.isFinite(supplied) || supplied<=0)
        ? amount : Math.max(0,Number.isFinite(supplied)?supplied:0);
      return sum + Math.min(refunded,amount);
    },0);
    return {gross,refunded:refundAmount,net:Math.max(0,gross-refundAmount),captured};
  }
  const accountPaidRows = () => paymentRows.filter(validPaidPayment);
  const amountTotal = (rows, field = 'amount') => rows.reduce((sum,row) => sum + (Number(row[field]) || 0),0);
  function allocation(rows, field) {
    if (rows.some(row => row[field] == null || !Number.isFinite(Number(row[field])))) return null;
    return amountTotal(rows, field);
  }
  const displayAllocation = (value,currency) => value == null ? '—' : money(value,currency);
  function eventKey(row) {
    const registrationId=String(row.event_registration_id || '');
    const eventId=row.event_id || row.event_registration_event_id || accountRegistrationEvents.get(registrationId);
    return eventId ? String(eventId) : 'unlinked-registration:'+(registrationId || String(row.id || 'unknown'));
  }
  function eventGroups() {
    const groups=new Map();
    for (const ev of accountEvents) {
      const key=String(ev.id);
      groups.set(key,{key,title:String(ev.title || 'Untitled event'),starts_at:ev.starts_at || '',rows:[]});
    }
    for (const row of paymentRows.filter(r=>r.purpose==='event_registration')) {
      const key=eventKey(row);
      if (!groups.has(key)) groups.set(key,{key,title:row.event_title || 'Event (verify registration link)', rows:[]});
      groups.get(key).rows.push(row);
    }
    return Array.from(groups.values()).sort((a,b)=>(b.starts_at || '').localeCompare(a.starts_at || '') || a.title.localeCompare(b.title));
  }
  function accountCategoryReport() {
    const currency = paymentSummary.currency || 'INR';
    const paid = accountPaidRows();
    const individual = paid.filter(row => row.purpose === 'mentor_booking');
    const compare = paid.filter(row => row.purpose === 'compare_live');
    const mentoring = individual.concat(compare);
    const events = paid.filter(row => row.purpose === 'event_registration');
    const ledger=financeLedger();
    const reportedTotal = Number(paymentSummary.total_gross_earnings);
    const ledgerTotal = amountTotal(paid);
    // Report a mismatch only for active fulfilled payments. Refunded history
    // legitimately makes the long-term gross higher than the current summary.
    const reconciled = Number.isFinite(reportedTotal) && Math.abs(reportedTotal-ledgerTotal) < 0.011;
    const accountButton = (key, title, description, value) => `
      <button type="button" class="pn5-fin-accounts-row" data-pn5-account-detail="${esc(key)}">
        <span><strong>${esc(title)}</strong><small>${esc(description)}</small></span>
        <span><strong>${value}</strong><small>View details →</small></span>
      </button>`;
    const card = (label,number,description) => `<article class="pn5-fin-card"><span>${esc(label)}</span>
      <strong>${number}</strong><small>${esc(description)}</small></article>`;

    if (accountDetail !== 'all') {
      let rows=[], title='', description='';
      if (accountDetail === 'individual') {
        return `<div class="pn5-fin-accounts-head"><button type="button" class="pn5-fin-action" data-pn5-account-back>← All categories</button>
          <h3>Individual mentoring</h3><p>Quick and Deep Dive sessions, grouped without double-counting collections.</p></div>
          <div class="pn5-fin-accounts-list">
          ${accountButton('quick','Quick · 15 min','View payments and bookings',money(financeLedger(paymentRows.filter(r=>r.purpose==='mentor_booking' && categoryLabel(r)==='Quick')).net,currency))}
          ${accountButton('deep','Deep Dive · 30 min','View payments and bookings',money(financeLedger(paymentRows.filter(r=>r.purpose==='mentor_booking' && categoryLabel(r)==='Deep Dive')).net,currency))}
          </div>`;
      } else if (accountDetail === 'quick' || accountDetail === 'deep') {
        rows=paymentRows.filter(row=>row.purpose==='mentor_booking' && categoryLabel(row)===(accountDetail==='quick'?'Quick':'Deep Dive'));
        title=accountDetail==='quick'?'Quick mentoring':'Deep Dive mentoring';
        description='All payment attempts, booking status and references';
      } else if (accountDetail === 'compare') {
        rows=paymentRows.filter(row=>row.purpose==='compare_live');
        title='Compare Live'; description='Two mentors · one student payment per booking';
      } else if (accountDetail === 'events') {
        return `<div class="pn5-fin-accounts-head"><button type="button" class="pn5-fin-action" data-pn5-account-back>← All categories</button>
          <h3>Events</h3><p>Select an event to see its individual transactions.</p></div>
          ${accountEventsStatus==='error' ? '<p class="pn5-fin-audit-warning">The event catalog could not load. Only events connected to payment records are listed here.</p>' : ''}
          <div class="pn5-fin-accounts-list">${eventGroups().map(group => {
            const ledger=financeLedger(group.rows);
            return accountButton('event:'+group.key,group.title,
              `${group.rows.length} transaction(s) · Refunded ${money(ledger.refunded,currency)}`,
              money(ledger.net,currency));
          }).join('') || '<div class="pn5-fin-empty">No events found.</div>'}</div>`;
      } else if (accountDetail.startsWith('event:')) {
        const group=eventGroups().find(group=>group.key===accountDetail.slice(6));
        rows=group?.rows || [];title=group?.title || 'Event';description='All payments and registration statuses for this event';
      } else if (accountDetail === 'communities') {
        return `<div class="pn5-fin-accounts-head"><button type="button" class="pn5-fin-action" data-pn5-account-back>← All categories</button>
          <h3>Communities</h3><p>Communities are currently managed as memberships. A community paid-checkout and settlement ledger
          has not been verified in this implementation, so no community financial amounts are reported.</p></div>`;
      }
      const ledger=financeLedger(rows);
      return `<div class="pn5-fin-accounts-head"><button type="button" class="pn5-fin-action" data-pn5-account-back>← ${accountDetail==='quick'||accountDetail==='deep'?'Individual mentoring': accountDetail.startsWith('event:')?'Events':'All categories'}</button>
        <h3>${esc(title)}</h3><p>${esc(description)}</p></div>
        <div class="pn5-fin-grid pn5-fin-grid--3">
        ${card('Gross collected',money(ledger.gross,currency),'Original captured payment history')}
        ${card('Refunded',money(ledger.refunded,currency),'Confirmed recorded refunds')}
        ${card('Net collected',money(ledger.net,currency),'Gross minus processed refunds')}
        </div><div class="pn5-fin-section-head"><div><span>TRANSACTIONS</span><h3>${rows.length} payment record(s)</h3></div></div>${paymentTransactionsHTML(rows)}`;
    }
    return `<div class="pn5-fin-accounts-head"><h3>Accounting breakdown</h3>
        <p>Select a category to see where each amount comes from and open its real transactions.</p></div>
      ${!reconciled ? `<p class="pn5-fin-audit-warning" role="alert">Accounting reconciliation needed: summary gross
        ${Number.isFinite(reportedTotal)?money(reportedTotal,currency):'unavailable'} does not match the currently loaded verified
        transaction list (${money(ledgerTotal,currency)}). Do not use these figures for settlement until checked.</p>` : ''}
      <div class="pn5-fin-grid pn5-fin-grid--4">
        ${card('Gross collected', money(ledger.gross,currency),'All captured payments, including later refunds')}
        ${card('Refunded',money(ledger.refunded,currency),'Recorded successful refunds')}
        ${card('Net collected',money(ledger.net,currency),'Gross minus completed refunds')}
        ${card('Mentor dues',money(paymentSummary.mentor_due,currency),'Completed, eligible and unpaid only')}
      </div>
      <div class="pn5-fin-grid pn5-fin-grid--3">
        ${card('Mentor share allocated',displayAllocation(allocation(mentoring,'mentor_amount'),currency),'Allocated from paid mentoring; not yet all due')}
        ${card('PeerNest share allocated',displayAllocation(allocation(mentoring,'platform_amount'),currency),'Expected platform share, subject to session outcome')}
        ${card('PeerNest revenue earned',money(paymentSummary.peerNest_retained,currency),'Recognized after completed services')}
      </div>
      <div class="pn5-fin-accounts-list">
        <div class="pn5-fin-accounts-group"><h4>Mentoring</h4>
          <p>Verified mentoring gross ${money(amountTotal(mentoring),currency)}</p>
          ${accountButton('individual','Individual mentoring','Quick and Deep Dive · '+individual.length+' collected payment(s)',money(amountTotal(individual),currency))}
          ${accountButton('compare','Compare Live',compare.length+' collected payment(s)',money(amountTotal(compare),currency))}</div>
        <div class="pn5-fin-accounts-group"><h4>Events</h4>
          <p>Gross ${money(financeLedger(paymentRows.filter(r=>r.purpose==='event_registration')).gross,currency)} · Net ${money(financeLedger(paymentRows.filter(r=>r.purpose==='event_registration')).net,currency)} · Organizer: PeerNest</p>
          ${accountButton('events','Individual events',eventGroups().length+' events · open each event’s transaction history',money(financeLedger(paymentRows.filter(r=>r.purpose==='event_registration')).net,currency))}</div>
        <div class="pn5-fin-accounts-group"><h4>Communities</h4>
          ${accountButton('communities','Memberships','No verified paid-community checkout ledger','Not configured')}</div>
      </div>
      <p class="pn5-fin-note">Gross, allocation and due are different numbers. Paid or upcoming bookings are not mentor dues
      until a session is completed and the earning becomes eligible. PeerNest is the event organizer; no third-party organizer payouts are calculated.</p>`;
  }

  function mentorPaymentsHTML(
    rows
  ) {

    if (!rows.length) {

      return `
        <div class="pn5-fin-empty">
          No matching mentor payments.
        </div>
      `;

    }


    return `
      <div class="pn5-fin-table-wrap">

        <table class="pn5-fin-table">

          <thead>

            <tr>
              <th>Student</th>
              <th>Mentor</th>
              <th>Session</th>
              <th>Gross</th>
              <th>Mentor 51%</th>
              <th>PeerNest 49%</th>
              <th>Payment</th>
              <th>Earning</th>
              <th>Razorpay</th>
            </tr>

          </thead>

          <tbody>

            ${rows
              .map(
                row => `
                  <tr>

                    <td>

                      <strong>
                        ${esc(
                          row.student_name ||
                          'Student'
                        )}
                      </strong>

                      <small>
                        ${esc(
                          row.student_email ||
                          ''
                        )}
                      </small>

                    </td>


                    <td>

                      <strong>
                        ${esc(
                          row.mentor_name ||
                          'PeerNest Mentor'
                        )}
                      </strong>

                      <small>
                        ${esc(
                          row.mentor_email ||
                          ''
                        )}
                      </small>

                    </td>


                    <td>

                      ${esc(
                        dateText(
                          row.session_starts_at
                        )
                      )}

                      <small>
                        ${esc(
                          row.booking_status ||
                          ''
                        )}
                      </small>

                    </td>


                    <td>
                      ${money(
                        row.amount,
                        row.currency
                      )}
                    </td>


                    <td>
                      <strong>
                        ${money(
                          row.mentor_amount,
                          row.currency
                        )}
                      </strong>
                    </td>


                    <td>
                      ${money(
                        row.platform_amount,
                        row.currency
                      )}
                    </td>


                    <td>
                      ${pill(
                        row.status
                      )}
                    </td>


                    <td>

                      ${pill(
                        row.earning_status ||
                        'not_earned'
                      )}

                      ${
                        row.payout_reference
                          ? `
                            <small>
                              ${esc(
                                row.payout_reference
                              )}
                            </small>
                          `
                          : ''
                      }

                    </td>


                    <td>

                      <small>
                        Order:
                        ${esc(
                          row.provider_order_id ||
                          row.razorpay_order_id ||
                          '—'
                        )}
                      </small>

                      <small>
                        Payment:
                        ${esc(
                          row.provider_payment_id ||
                          row.razorpay_payment_id ||
                          '—'
                        )}
                      </small>

                    </td>

                  </tr>
                `
              )
              .join('')}

          </tbody>

        </table>

      </div>
    `;

  }


  function eventPaymentsHTML(
    rows
  ) {

    if (!rows.length) {

      return `
        <div class="pn5-fin-empty">
          No matching event payments.
        </div>
      `;

    }


    return `
      <div class="pn5-fin-table-wrap">

        <table class="pn5-fin-table">

          <thead>

            <tr>
              <th>Event</th>
              <th>Student</th>
              <th>Amount</th>
              <th>Payment</th>
              <th>Registration</th>
              <th>Razorpay</th>
            </tr>

          </thead>

          <tbody>

            ${rows
              .map(
                row => `
                  <tr>

                    <td>

                      <strong>
                        ${esc(
                          row.event_title ||
                          'Event'
                        )}
                      </strong>

                      <small>
                        ${esc(
                          row.event_id ||
                          ''
                        )}
                      </small>

                    </td>


                    <td>

                      <strong>
                        ${esc(
                          row.student_name ||
                          'Student'
                        )}
                      </strong>

                      <small>
                        ${esc(
                          row.student_email ||
                          ''
                        )}
                      </small>

                    </td>


                    <td>
                      <strong>
                        ${money(
                          row.amount,
                          row.currency
                        )}
                      </strong>
                    </td>


                    <td>
                      ${pill(
                        row.status
                      )}
                    </td>


                    <td>
                      ${pill(
                        row.event_registration_status ||
                        'unknown'
                      )}
                    </td>


                    <td>

                      <small>
                        ${esc(
                          row.provider_payment_id ||
                          row.razorpay_payment_id ||
                          '—'
                        )}
                      </small>

                    </td>

                  </tr>
                `
              )
              .join('')}

          </tbody>

        </table>

      </div>
    `;

  }



  /* PN5_PAYMENT_CENTER_PRICING_V1 */

  let mentorPricingRow =
    null;


  function mentorPricingHTML() {

    return `
      <section class="pn5-pricing-card">

        <div class="pn5-pricing-card__head">

          <span>
            GLOBAL PRICING
          </span>

          <h4>
            Mentor Session Pricing
          </h4>

          <p>
            These prices apply to every mentor on PeerNest.
            Event pricing is managed separately inside each event.
          </p>

        </div>


        <div class="pn5-pricing-grid">

          <label class="pn5-pricing-field">

            <span>
              15 minute session
            </span>

            <div class="pn5-pricing-input">

              <b data-pn5-price-currency>
                INR
              </b>

              <input
                type="number"
                min="1"
                step="1"
                inputmode="decimal"
                placeholder="Set price"
                data-pn5-price-15
              >

            </div>

          </label>


          <label class="pn5-pricing-field">

            <span>
              30 minute session
            </span>

            <div class="pn5-pricing-input">

              <b data-pn5-price-currency>
                INR
              </b>

              <input
                type="number"
                min="1"
                step="1"
                inputmode="decimal"
                placeholder="Set price"
                data-pn5-price-30
              >

            </div>

          </label>


          <label class="pn5-pricing-field">

            <span>
              Compare Live · 2 mentors (30 min)
            </span>

            <div class="pn5-pricing-input">

              <b data-pn5-price-currency>
                INR
              </b>

              <input
                type="number"
                min="1"
                step="1"
                inputmode="decimal"
                placeholder="Set Compare price"
                data-pn5-price-compare
              >

            </div>

          </label>


          <label class="pn5-pricing-field">

            <span>
              Currency
            </span>

            <select data-pn5-pricing-currency>

              <option value="INR">
                INR — Indian Rupee
              </option>

              <option value="USD">
                USD — US Dollar
              </option>

              <option value="EUR">
                EUR — Euro
              </option>

              <option value="GBP">
                GBP — British Pound
              </option>

              <option value="AED">
                AED — UAE Dirham
              </option>

              <option value="SGD">
                SGD — Singapore Dollar
              </option>

              <option value="AUD">
                AUD — Australian Dollar
              </option>

              <option value="CAD">
                CAD — Canadian Dollar
              </option>

            </select>

          </label>

        </div>


        <div class="pn5-pricing-actions">

          <button
            type="button"
            class="pn5-pricing-save"
            data-pn5-pricing-save
          >
            Save Pricing
          </button>


          <span
            class="pn5-pricing-message"
            data-pn5-pricing-message
          >
            Loading pricing…
          </span>

        </div>

      </section>
    `;

  }


  function setPricingMessage(
    panel,
    message,
    error = false
  ) {

    const node =
      panel.querySelector(
        '[data-pn5-pricing-message]'
      );


    if (!node) {
      return;
    }


    node.textContent =
      message;


    node.classList.toggle(
      'is-error',
      error
    );

  }


  function updatePricingCurrencyLabels(
    panel
  ) {

    const currency =
      panel.querySelector(
        '[data-pn5-pricing-currency]'
      )?.value ||
      'INR';


    panel.querySelectorAll(
      '[data-pn5-price-currency]'
    )
      .forEach(
        label => {

          label.textContent =
            currency;

        }
      );

  }


  async function loadMentorPricing(
    panel
  ) {

    if (!panel) {
      return;
    }


    setPricingMessage(
      panel,
      'Loading pricing…'
    );


    try {

      const pricing =
        await window.PeerNestDB
          .request(
            'mentor_session_pricing',
            'id=eq.1&select=id,price_15,price_30,price_compare,currency,updated_at'
          );


      mentorPricingRow =
        Array.isArray(
          pricing
        )
          ? pricing[0]
          : null;


      if (!mentorPricingRow) {

        throw new Error(
          'Global mentor pricing record was not found.'
        );

      }


      const price15 =
        panel.querySelector(
          '[data-pn5-price-15]'
        );


      const price30 =
        panel.querySelector(
          '[data-pn5-price-30]'
        );


      const priceCompare =
        panel.querySelector(
          '[data-pn5-price-compare]'
        );


      const currency =
        panel.querySelector(
          '[data-pn5-pricing-currency]'
        );


      if (price15) {

        price15.value =
          mentorPricingRow.price_15 ??
          '';

      }


      if (price30) {

        price30.value =
          mentorPricingRow.price_30 ??
          '';

      }


      if (priceCompare) {

        priceCompare.value =
          mentorPricingRow.price_compare ??
          '';

      }


      if (currency) {

        currency.value =
          String(
            mentorPricingRow.currency ||
            'INR'
          ).toUpperCase();

      }


      updatePricingCurrencyLabels(
        panel
      );


      setPricingMessage(
        panel,
        mentorPricingRow.price_15 &&
        mentorPricingRow.price_30
          ? 'Quick, Deep Dive and Compare Live pricing are configured. Paid checkout is active.'
          : 'Configure Quick, Deep Dive and Compare Live prices.'
      );

    }


    catch (error) {

      console.error(
        '[PeerNest Finance] pricing load:',
        error
      );


      setPricingMessage(
        panel,
        error?.message ||
        'Could not load mentor pricing.',
        true
      );

    }

  }


  async function saveMentorPricing(
    panel
  ) {

    const price15 =
      panel.querySelector(
        '[data-pn5-price-15]'
      );


    const price30 =
      panel.querySelector(
        '[data-pn5-price-30]'
      );


    const priceCompare =
      panel.querySelector(
        '[data-pn5-price-compare]'
      );


    const currency =
      panel.querySelector(
        '[data-pn5-pricing-currency]'
      );


    const button =
      panel.querySelector(
        '[data-pn5-pricing-save]'
      );


    if (
      !price15 ||
      !price30 ||
      !priceCompare ||
      !currency ||
      !button
    ) {

      return;

    }


    const value15 =
      Number(
        price15.value
      );


    const value30 =
      Number(
        price30.value
      );


    if (
      !Number.isFinite(
        value15
      ) ||
      value15 <= 0
    ) {

      setPricingMessage(
        panel,
        'Enter a valid 15-minute price.',
        true
      );


      price15.focus();

      return;

    }


    if (
      !Number.isFinite(
        value30
      ) ||
      value30 <= 0
    ) {

      setPricingMessage(
        panel,
        'Enter a valid 30-minute price.',
        true
      );


      price30.focus();

      return;

    }


    const compareRaw =
      priceCompare.value.trim();


    const valueCompare =
      compareRaw === ''
        ? null
        : Number(compareRaw);


    if (
      valueCompare !== null &&
      (
        !Number.isFinite(valueCompare) ||
        valueCompare <= 0
      )
    ) {

      setPricingMessage(
        panel,
        'Enter a valid Compare Live price, or leave it blank.',
        true
      );

      priceCompare.focus();

      return;

    }


    button.disabled =
      true;

    button.textContent =
      'Saving…';


    setPricingMessage(
      panel,
      'Updating global pricing…'
    );


    try {

      const result =
        await window.PeerNestDB
          .request(
            'mentor_session_pricing',
            'id=eq.1',
            {
              method:'PATCH',

              headers:{
                Prefer:
                  'return=representation'
              },

              body:{
                price_15:
                  value15,

                price_30:
                  value30,

                price_compare:
                  valueCompare,

                currency:
                  currency.value
              }
            }
          );


      mentorPricingRow =
        Array.isArray(result)
          ? (
              result[0] ||
              mentorPricingRow
            )
          : mentorPricingRow;


      setPricingMessage(
        panel,
        'Pricing saved. Quick, Deep Dive and Compare Live use the configured global rates.'
      );

    }


    catch (error) {

      console.error(
        '[PeerNest Finance] pricing save:',
        error
      );


      setPricingMessage(
        panel,
        error?.message ||
        'Could not save mentor pricing.',
        true
      );

    }


    finally {

      button.disabled =
        false;

      button.textContent =
        'Save Pricing';

    }

  }


  function renderPaymentCenter(panel) {
    if (!panel) return;
    const rows = filteredPaymentRows();
    const modes = [
      ['accounts', 'Breakdown'],
      ['transactions', 'Transactions'],
      ['dues', 'Dues'],
      ['payouts', 'Payout History'],
      ['refunds', 'Refunds'],
      ['issues', 'Issues'],
      ['revenue', 'Reports'],
      ['pricing', 'Pricing']
    ];
    panel.innerHTML = `
      <div class="pn5-payment-center__head"><div>
        <span>PEERNEST FINANCE</span>
        <h3>Payments workspace</h3>
        <p>Money in, money due, payouts and refunds. One clear record for every transaction.</p>
      </div></div>
      <div class="pn5-fin-grid pn5-fin-grid--4">
        <article class="pn5-fin-card"><span>GROSS COLLECTED</span><strong>${money(financeLedger().gross,paymentSummary.currency)}</strong><small>Captured payment history</small></article>
        <article class="pn5-fin-card"><span>REFUNDED</span><strong>${money(financeLedger().refunded,paymentSummary.currency)}</strong><small>Completed refund records</small></article>
        <article class="pn5-fin-card pn5-fin-card--primary"><span>NET COLLECTED</span><strong>${money(financeLedger().net,paymentSummary.currency)}</strong><small>Gross minus refunds</small></article>
        <article class="pn5-fin-card"><span>MENTOR DUES</span><strong>${money(paymentSummary.mentor_due,paymentSummary.currency)}</strong><small>Eligible and unpaid only</small></article>
      </div>
      <p class="pn5-fin-note" role="note">PeerNest revenue earned: <strong>${money(paymentSummary.peerNest_retained,paymentSummary.currency)}</strong>
        · Mentor payouts recorded: <strong>${money(paymentSummary.mentor_paid,paymentSummary.currency)}</strong>.
        See Breakdown for mentor allocations and event-by-event accounting.</p>
      <div class="pn5-fin-tabs">
        ${modes.map(([id, label]) => `<button type="button" class="${paymentMode===id ? 'is-active' : ''}" data-pn5-payment-mode="${id}">${label}</button>`).join('')}
      </div>
      ${paymentMode === 'transactions' ? `
        <div class="pnadm-mentor-toolbar">
          <select data-pn5-payment-category aria-label="Transaction category">
            <option value="all" ${paymentCategory==='all'?'selected':''}>All transactions</option>
            <option value="mentoring" ${paymentCategory==='mentoring'?'selected':''}>Mentoring</option>
            <option value="compare" ${paymentCategory==='compare'?'selected':''}>Compare Live</option>
            <option value="events" ${paymentCategory==='events'?'selected':''}>Events</option>
          </select>
          <input class="pn5-fin-search" type="search" value="${esc(paymentSearch)}" placeholder="Search student, mentor, payment ID…" data-pn5-payment-search>
        </div>
      ` : ''}
      ${(paymentMode === 'dues' || paymentMode === 'payouts') ? `
        <div class="pnadm-mentor-toolbar"><button type="button" data-pn5-payment-refresh>Refresh ${paymentMode}</button></div>
      ` : ''}
      <div class="pn5-payment-center__body">
        ${paymentMode==='accounts' ? accountCategoryReport()
        :paymentMode==='transactions' ? paymentTransactionsHTML(rows)
        :paymentMode==='dues' ? paymentDuesHTML()
        :paymentMode==='payouts' ? paymentPayoutsHTML()
        :paymentMode==='refunds' ? paymentRefundsHTML(rows)
        :paymentMode==='issues' ? paymentIssuesHTML(paymentRows)+'<div data-pnx-admin-root></div>'
        :paymentMode==='pricing' ? mentorPricingHTML()
        :paymentRevenueHTML()}
      </div>`;

    if (paymentMode === 'issues') {
      window.PeerNestPass2Exceptions?.mount(panel.querySelector('[data-pnx-admin-root]'));
    }

    panel.querySelectorAll('[data-pn5-payment-mode]').forEach(button => {
      button.addEventListener('click', () => {
        paymentMode = button.dataset.pn5PaymentMode;
        if (paymentMode === 'accounts') accountDetail = 'all';
        renderPaymentCenter(panel);
        if (paymentMode === 'dues' || paymentMode === 'payouts') {
          loadPaymentExtra(panel, paymentMode);
        }
        if (paymentMode === 'pricing') loadMentorPricing(panel);
      });
    });

    panel.querySelectorAll('[data-pn5-account-detail]').forEach(button => {
      button.addEventListener('click', () => {
        accountDetail = button.dataset.pn5AccountDetail || 'all';
        renderPaymentCenter(panel);
      });
    });
    panel.querySelector('[data-pn5-account-back]')?.addEventListener('click', () => {
      accountDetail = accountDetail.startsWith('event:') ? 'events' : (accountDetail==='quick'||accountDetail==='deep') ? 'individual' : 'all';
      renderPaymentCenter(panel);
    });
    panel.querySelector('[data-pn5-payment-search]')?.addEventListener('input', event => {
      paymentSearch = event.target.value || '';
      renderPaymentCenter(panel);
      const input = panel.querySelector('[data-pn5-payment-search]');
      input?.focus();
      if (input) input.setSelectionRange(input.value.length,input.value.length);
    });
    panel.querySelector('[data-pn5-payment-category]')?.addEventListener('change', event => {
      paymentCategory = event.target.value || 'all';
      renderPaymentCenter(panel);
    });
    panel.querySelector('[data-pn5-payment-refresh]')?.addEventListener('click',() => {
      loadPaymentExtra(panel,paymentMode);
    });
    panel.querySelectorAll('[data-pn5-pay-mentor]').forEach(button => {
      button.addEventListener('click', () => {
        const mentorId = button.dataset.pn5PayMentor;
        // Existing manual UPI payout modal is also loaded on the admin dashboard.
        // Opening the form does NOT mark an earning paid or submit a transfer.
        if (mentorId && typeof window.PeerNestManualPayout?.open === 'function') {
          window.PeerNestManualPayout.open(mentorId);
        } else {
          window.alert('The existing manual payout form is unavailable. Refresh the Admin dashboard and retry.');
        }
      });
    });
    if (paymentMode === 'pricing') {
      panel.querySelector('[data-pn5-pricing-currency]')?.addEventListener('change',() => updatePricingCurrencyLabels(panel));
      panel.querySelector('[data-pn5-pricing-save]')?.addEventListener('click',() => saveMentorPricing(panel));
    }
  }


  async function mountPaymentCenter() {

    /* PN_PAYMENT_CENTER_SINGLE_FLIGHT */

    if (
      window.__pnPaymentCenterLoading
    ) {
      return;
    }


    window.__pnPaymentCenterLoading =
      true;


    /*
      Safety release only.
      The observer no longer watches child DOM mutations,
      so repeated self-rendering cannot occur.
    */
    window.setTimeout(
      () => {
        window.__pnPaymentCenterLoading =
          false;
      },
      5000
    );



    const dashboard =
      document.querySelector(
        '[data-pnadm-dashboard]'
      );

    if (!dashboard) {
      return;
    }


    const body =
      dashboard.querySelector(
        '[data-pnops-body]'
      );


    if (!body) {
      return;
    }


    let panel =
      body.querySelector(
        '[data-pn5-payment-center]'
      );


    if (!panel) {

      panel =
        document.createElement(
          'section'
        );

      panel.className =
        'pn5-payment-center';

      panel.setAttribute(
        'data-pn5-payment-center',
        ''
      );

      body.prepend(
        panel
      );

    }


    panel.innerHTML = `
      <div class="pn5-fin-empty">
        Loading payment center…
      </div>
    `;


    try {

      const result =
        await Promise.all([
          rpc(
            'admin_list_payment_operations_v2'
          ),

          rpc(
            'admin_finance_summary_v2'
          )
        ]);


      paymentRows =
        Array.isArray(
          result[0]
        )
          ? result[0]
          : [];

      paymentSummary = result[1] || {};
      if (typeof paymentSummary.total_gross_earnings === 'undefined') {
        throw new Error('Finance summary is unavailable. Please refresh; amounts have not been set to zero.');
      }


      renderPaymentCenter(
        panel
      );
      if (accountEventsStatus === 'loading') {
        void loadFinanceEventCatalog().then(() => {
          if (panel.isConnected && paymentMode === 'accounts') renderPaymentCenter(panel);
        });
      }

    }
    catch (error) {

      panel.innerHTML = `
        <div class="pn5-fin-empty">
          ${esc(
            error?.message ||
            'Could not load payment center.'
          )}
        </div>
      `;

    }

  }


  /* ========================================================
     ADMIN MENTOR FINANCE
  ======================================================== */

  function filteredMentors() {

    const query =
      mentorSearch
        .trim()
        .toLowerCase();


    if (!query) {
      return mentorRows;
    }


    return mentorRows.filter(
      mentor => {

        try {

          return JSON.stringify(
            mentor
          )
            .toLowerCase()
            .includes(
              query
            );

        }
        catch (_) {

          return false;

        }

      }
    );

  }


  async function openMentorDetail(
    mentorId,
    panel
  ) {

    const detail =
      panel.querySelector(
        '[data-pn5-mentor-finance-detail]'
      );


    if (!detail) {
      return;
    }


    detail.hidden =
      false;

    detail.innerHTML = `
      <div class="pn5-fin-empty">
        Loading mentor details…
      </div>
    `;


    try {

      const mentor =
        await rpc(
          'admin_get_mentor_finance_detail_v2',
          {
            p_mentor_id:
              mentorId
          }
        );


      const sessions =
        Array.isArray(
          mentor.sessions
        )
          ? mentor.sessions
          : [];


      detail.innerHTML = `
        <div class="pn5-mentor-detail__head">

          <div>

            <span>
              MENTOR DETAIL
            </span>

            <h3>
              ${esc(
                mentor.display_name ||
                mentor.account_name ||
                'PeerNest Mentor'
              )}
            </h3>

            <p>
              ${esc(
                mentor.email ||
                ''
              )}
              ·
              ${esc(
                mentor.college_name ||
                '—'
              )}
              ·
              ${esc(
                mentor.program_name ||
                '—'
              )}
            </p>

          </div>


          <button
            type="button"
            data-pn5-mentor-detail-close
          >
            Close
          </button>

        </div>


        <div class="pn5-fin-grid pn5-fin-grid--3">

          <article class="pn5-fin-card">
            <span>TOTAL EARNINGS</span>
            <strong>
              ${money(
                mentor.total_earnings,
                'INR'
              )}
            </strong>
          </article>

          <article class="pn5-fin-card">
            <span>PAID</span>
            <strong>
              ${money(
                mentor.paid,
                'INR'
              )}
            </strong>
          </article>

          <article class="pn5-fin-card">
            <span>TO BE PAID</span>
            <strong>
              ${money(
                mentor.to_be_paid,
                'INR'
              )}
            </strong>
          </article>

        </div>


        ${
          sessions.length
            ? `
              <div class="pn5-fin-table-wrap">

                <table class="pn5-fin-table">

                  <thead>

                    <tr>
                      <th>Date</th>
                      <th>Student</th>
                      <th>Session</th>
                      <th>Gross</th>
                      <th>Mentor</th>
                      <th>Earning</th>
                      <th>Payout</th>
                    </tr>

                  </thead>

                  <tbody>

                    ${sessions
                      .map(
                        session => `
                          <tr>

                            <td>
                              ${esc(
                                dateText(
                                  session.starts_at
                                )
                              )}
                            </td>

                            <td>

                              <strong>
                                ${esc(
                                  session.student_name ||
                                  'Student'
                                )}
                              </strong>

                              <small>
                                ${esc(
                                  session.student_email ||
                                  ''
                                )}
                              </small>

                            </td>

                            <td>
                              ${pill(
                                session.booking_status
                              )}
                            </td>

                            <td>
                              ${money(
                                session.gross_amount,
                                'INR'
                              )}
                            </td>

                            <td>
                              <strong>
                                ${money(
                                  session.mentor_amount,
                                  'INR'
                                )}
                              </strong>
                            </td>

                            <td>
                              ${pill(
                                session.earning_status ||
                                'none'
                              )}
                            </td>

                            <td>
                              ${esc(
                                session.payout_reference ||
                                '—'
                              )}
                            </td>

                          </tr>
                        `
                      )
                      .join('')}

                  </tbody>

                </table>

              </div>
            `
            : `
              <div class="pn5-fin-empty">
                No mentor sessions yet.
              </div>
            `
        }
      `;


      detail.querySelector(
        '[data-pn5-mentor-detail-close]'
      )
        ?.addEventListener(
          'click',
          () => {

            detail.hidden =
              true;

          }
        );

    }
    catch (error) {

      detail.innerHTML = `
        <div class="pn5-fin-empty">
          ${esc(
            error?.message ||
            'Could not load mentor details.'
          )}
        </div>
      `;

    }

  }


  function renderAdminMentorFinance(
    panel
  ) {

    const rows =
      filteredMentors();


    panel.innerHTML = `
      <div class="pnadm-card__head">

        <div>

          <span>
            MENTOR FINANCE
          </span>

          <h3>
            Sessions, earnings & payouts
          </h3>

        </div>

      </div>


      <div class="pnadm-mentor-toolbar">

        <input
          type="search"
          placeholder="Search name, email, college, program or mentor ID…"
          value="${esc(
            mentorSearch
          )}"
          data-pn5-mentor-finance-search
        >

        <button
          type="button"
          data-pn5-mentor-finance-refresh
        >
          Refresh
        </button>

      </div>


      ${
        rows.length
          ? `
            <div class="pn5-fin-table-wrap">

              <table class="pn5-fin-table">

                <thead>

                  <tr>
                    <th>Mentor</th>
                    <th>College / Program</th>
                    <th>Sessions</th>
                    <th>Total Earnings</th>
                    <th>Paid</th>
                    <th>To Be Paid</th>
                    <th>Status</th>
                    <th></th>
                  </tr>

                </thead>

                <tbody>

                  ${rows
                    .map(
                      mentor => `
                        <tr>

                          <td>

                            <strong>
                              ${esc(
                                mentor.display_name ||
                                mentor.account_name ||
                                'PeerNest Mentor'
                              )}
                            </strong>

                            <small>
                              ${esc(
                                mentor.email ||
                                ''
                              )}
                            </small>

                            <small>
                              ID:
                              ${esc(
                                mentor.mentor_id ||
                                mentor.id
                              )}
                            </small>

                          </td>


                          <td>

                            ${esc(
                              mentor.college_name ||
                              '—'
                            )}

                            <small>
                              ${esc(
                                mentor.program_name ||
                                '—'
                              )}
                            </small>

                          </td>


                          <td>

                            <strong>
                              ${esc(
                                mentor.completed_sessions ||
                                0
                              )}
                              completed
                            </strong>

                            <small>
                              ${esc(
                                mentor.upcoming_sessions ||
                                0
                              )}
                              upcoming ·
                              ${esc(
                                mentor.cancelled_sessions ||
                                0
                              )}
                              cancelled
                            </small>

                          </td>


                          <td>
                            <strong>
                              ${money(
                                mentor.total_earnings,
                                mentor.currency
                              )}
                            </strong>
                          </td>


                          <td>
                            ${money(
                              mentor.paid,
                              mentor.currency
                            )}
                          </td>


                          <td>
                            <strong>
                              ${money(
                                mentor.to_be_paid,
                                mentor.currency
                              )}
                            </strong>
                          </td>


                          <td>
                            ${pill(
                              mentor.status
                            )}
                          </td>


                          <td>

                            <button
                              type="button"
                              class="pn5-fin-action"
                              data-pn5-mentor-detail="${esc(
                                mentor.mentor_id ||
                                mentor.id
                              )}"
                            >
                              View
                            </button>

                          </td>

                        </tr>
                      `
                    )
                    .join('')}

                </tbody>

              </table>

            </div>
          `
          : `
            <div class="pn5-fin-empty">
              No mentors match this search.
            </div>
          `
      }


      <div
        class="pn5-mentor-detail"
        data-pn5-mentor-finance-detail
        hidden
      ></div>
    `;


    panel.querySelector(
      '[data-pn5-mentor-finance-search]'
    )
      ?.addEventListener(
        'input',
        event => {

          mentorSearch =
            event.target.value ||
            '';

          renderAdminMentorFinance(
            panel
          );

          const input =
            panel.querySelector(
              '[data-pn5-mentor-finance-search]'
            );

          input?.focus();

        }
      );


    panel.querySelector(
      '[data-pn5-mentor-finance-refresh]'
    )
      ?.addEventListener(
        'click',
        () =>
          loadAdminMentorFinance(
            panel
          )
      );


    panel.querySelectorAll(
      '[data-pn5-mentor-detail]'
    )
      .forEach(
        button => {

          button.addEventListener(
            'click',
            () =>
              openMentorDetail(
                button.dataset
                  .pn5MentorDetail,
                panel
              )
          );

        }
      );

  }


  async function loadAdminMentorFinance(
    panel
  ) {

    panel.innerHTML = `
      <div class="pn5-fin-empty">
        Loading mentor finance…
      </div>
    `;


    try {

      const result =
        await rpc(
          'admin_list_mentor_finance_v2'
        );


      mentorRows =
        Array.isArray(
          result
        )
          ? result
          : [];


      renderAdminMentorFinance(
        panel
      );

    }
    catch (error) {

      panel.innerHTML = `
        <div class="pn5-fin-empty">
          ${esc(
            error?.message ||
            'Could not load mentors.'
          )}
        </div>
      `;

    }

  }


  function mountMentorFinanceTab() {

    const mentorView =
      document.querySelector(
        '[data-pnadm-view="mentors"]'
      );


    if (!mentorView) {
      return;
    }


    const tabs =
      mentorView.querySelector(
        '.pnadm-mentor-tabs'
      );


    if (!tabs) {
      return;
    }


    if (
      tabs.querySelector(
        '[data-pn5-mentor-finance-tab]'
      )
    ) {
      return;
    }


    const button =
      document.createElement(
        'button'
      );

    button.type =
      'button';

    button.textContent =
      'Mentor Finance';

    button.setAttribute(
      'data-pn5-mentor-finance-tab',
      ''
    );

    tabs.appendChild(
      button
    );


    const panel =
      document.createElement(
        'section'
      );

    panel.className =
      'pnadm-card';

    panel.setAttribute(
      'data-pn5-mentor-finance-panel',
      ''
    );

    panel.hidden =
      true;

    mentorView.appendChild(
      panel
    );


    button.addEventListener(
      'click',
      () => {

        tabs.querySelectorAll(
          'button'
        )
          .forEach(
            tab =>
              tab.classList.remove(
                'is-active'
              )
          );


        button.classList.add(
          'is-active'
        );


        [
          ...mentorView.children
        ]
          .forEach(
            child => {

              if (
                child.matches?.(
                  '.pnadm-card'
                ) &&
                child !== panel
              ) {

                child.hidden =
                  true;

              }

            }
          );


        panel.hidden =
          false;


        loadAdminMentorFinance(
          panel
        );

      }
    );


    tabs.querySelectorAll(
      '[data-pnadm-mentor-tab]'
    )
      .forEach(
        tab => {

          tab.addEventListener(
            'click',
            () => {

              panel.hidden =
                true;

            }
          );

        }
      );

  }


  /* ========================================================
     INIT / WATCHERS
  ======================================================== */

  let paymentMountTimer =
    null;


  function schedulePaymentMount() {

    clearTimeout(
      paymentMountTimer
    );


    paymentMountTimer =
      setTimeout(
        () => {

          const title =
            document.querySelector(
              '[data-pnops-title]'
            );


          const activePayment =
            document.querySelector(
              '[data-pnops-tab="payments"].is-active'
            );


          if (
            activePayment ||
            String(
              title?.textContent ||
              ''
            )
              .trim()
              .toLowerCase() ===
              'payments'
          ) {

            mountPaymentCenter();

          }

        },
        350
      );

  }


  function init() {

    if (!window.PeerNestDB) {

      setTimeout(
        init,
        150
      );

      return;

    }


    renderMentorFinance();

    renderAdminOverview();

    mountMentorFinanceTab();

    schedulePaymentMount();


    if (started) {
      return;
    }


    started =
      true;


    document.addEventListener(
      'click',
      event => {

        if (
          event.target.closest(
            '[data-pnops-tab="payments"], [data-pnadm-view-key="payments"]'
          )
        ) {

          schedulePaymentMount();

        }


        if (
          event.target.closest(
            '[data-pnmd-view-link="earnings"]'
          )
        ) {

          setTimeout(
            renderMentorFinance,
            100
          );

        }

      }
    );


    const dashboard =
      document.querySelector(
        '[data-pnadm-dashboard]'
      );


    if (dashboard) {

      const observer =
        new MutationObserver(
          () => {

            mountMentorFinanceTab();

            schedulePaymentMount();

          }
        );


      observer.observe(
      document.documentElement,
      {
        attributes:true,
        subtree:true,
        attributeFilter:[
          'data-pnadm-active-operation'
        ]
      }
    );

    }

  }


  document.addEventListener(
    'peernest:supabase-ready',
    init
  );


  document.addEventListener(
    'peernest:dashboard-ready',
    init
  );


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  }
  else {

    init();

  }

})();
